import React from "react";
import { Minus, Plus } from "lucide-react";
import { Product } from "../../interfaces/sales";

export interface ProductSelectionStepProps {
  products: Product[];
  selectedProducts: Record<string, number>;
  onSelectionChange: (uuid: string, quantity: number) => void;
  refundTotal: number;
}

export const ProductSelectionStep: React.FC<ProductSelectionStepProps> = ({
  products,
  selectedProducts,
  onSelectionChange,
  refundTotal,
}) => {
  const parentProducts = products.filter((p) => !p.uuid_parent);

  const getAddons = (parentUuid: string): Product[] =>
    products.filter((p) => p.uuid_parent === parentUuid);

  const renderProductRow = (product: Product, isAddon: boolean) => {
    const cancelQty = selectedProducts[product.uuid] || 0;

    return (
      <div
        key={product.uuid}
        className={`flex items-center justify-between rounded-lg border p-3 ${
          isAddon ? "ml-6 border-dashed" : ""
        } ${cancelQty > 0 ? "border-red-200 bg-red-50" : "border-gray-200 bg-white"}`}
      >
        <div className="min-w-0 flex-1">
          <p
            className={`text-sm font-medium ${
              isAddon ? "text-gray-600" : "text-gray-900"
            }`}
          >
            {isAddon && "↳ "}
            {product.product}
          </p>
          <p className="text-xs text-gray-500">
            ${product.price.toFixed(2)} × {product.quantity} = $
            {product.subtotal.toFixed(2)}
          </p>
        </div>
        {isAddon ? (
          <span className="text-sm font-medium text-gray-500">
            {cancelQty} / {product.quantity}
          </span>
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={cancelQty <= 0}
              onClick={() => onSelectionChange(product.uuid, cancelQty - 1)}
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className="w-8 text-center text-sm font-medium">
              {cancelQty}
            </span>
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={cancelQty >= product.quantity}
              onClick={() => onSelectionChange(product.uuid, cancelQty + 1)}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Selecciona los productos y la cantidad que deseas cancelar.
      </p>
      <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
        {parentProducts.map((parent) => (
          <React.Fragment key={parent.uuid}>
            {renderProductRow(parent, false)}
            {getAddons(parent.uuid).map((addon) =>
              renderProductRow(addon, true),
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="flex items-center justify-between border-t border-gray-200 pt-3">
        <span className="text-sm font-medium text-gray-700">
          Total a reembolsar:
        </span>
        <span className="text-lg font-bold text-red-600">
          ${refundTotal.toFixed(2)}
        </span>
      </div>
    </div>
  );
};
